import type { CityConfig } from "../city-template/types";

export const louisvilleConfig: CityConfig = {
  city: "Louisville",
  state: "Kentucky",
  stateAbbr: "KY",
  slug: "louisville",
  route: "/cities/louisville",
  title: "Emergency Passport Louisville, KY | Fast Passport Renewal | Derby City",
  metaDescription: "Emergency passport and expedited passport services in Louisville, KY — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Jefferson County, Southern Indiana, and Derby City travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Louisville, KY",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Louisville and Southern Indiana travelers get their expedited passports as quickly as 24 hours. Serving downtown Louisville, the Highlands, St. Matthews, and cross-river Indiana communities. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Louisville%20Kentucky%20downtown%20skyline%20at%20golden%20hour%20with%20Ohio%20River%20waterfront%2C%20Big%20Four%20Bridge%20visible%20with%20warm%20sunset%20light%20reflecting%20on%20modern%20buildings%2C%20clean%20panoramic%20cityscape%20photography%2C%20no%20visible%20text%20or%20people%2C%20Derby%20City%20skyline%20with%20iconic%20downtown%20architecture%20and%20riverfront%2C%20wide%20panoramic%20view&width=1800&height=700&seq=louisville-hero-001&orientation=landscape",
  heroImageAlt: "Louisville Kentucky downtown skyline at golden hour with Ohio River waterfront and Big Four Bridge",
  geoPosition: "38.2527;-85.7585",
  geoPlacename: "Louisville, Kentucky",
  regionalAgency: {
    name: "Chicago Passport Agency",
    address: "77 W Jackson Blvd, Suite 1200, Chicago, IL 60604",
    distance: "Located in downtown Chicago — approximately 300 miles from Louisville via I-65",
  },
  nearbyCitiesZips: [
    { name: "Louisville", zips: "40202, 40203, 40204, 40205, 40206, 40207, 40208, 40209, 40210, 40211, 40212, 40213, 40214, 40215, 40216, 40217, 40218, 40219, 40220, 40221, 40222, 40223, 40228, 40229, 40231, 40232, 40233, 40241, 40242, 40243, 40245, 40250, 40251, 40252, 40253, 40255, 40256, 40257, 40258, 40259, 40261, 40266, 40268, 40269, 40270, 40272, 40280, 40281, 40282, 40283, 40285, 40287, 40289, 40290, 40291, 40292, 40293, 40294, 40295, 40296, 40297, 40298, 40299" },
    { name: "Jeffersonville (IN)", zips: "47130, 47131, 47132, 47133, 47134, 47144, 47190, 47199" },
    { name: "New Albany (IN)", zips: "47150, 47151" },
    { name: "Clarksville (IN)", zips: "47129, 47172" },
    { name: "St. Matthews", zips: "40207" },
    { name: "Jeffersontown", zips: "40299, 40220, 40223, 40291, 40228, 40241, 40242, 40243, 40245, 40250, 40255, 40256, 40257, 40258, 40259, 40269, 40299" },
    { name: "Shively", zips: "40216, 40256" },
    { name: "Shelbyville", zips: "40065, 40066" },
    { name: "Elizabethtown", zips: "42701, 42702" },
    { name: "Bardstown", zips: "40004, 40013" },
    { name: "Radcliff", zips: "40159, 40160, 40161, 40162, 40165, 40177" },
    { name: "Fort Knox", zips: "40121, 40122, 40162" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Derby City departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Lexington", route: "/cities/lexington" },
    { name: "Fast passport renewal in Bowling Green", route: "/cities/bowling-green" },
    { name: "Emergency passport help in Florence", route: "/cities/florence" },
    { name: "Urgent passport services in Covington", route: "/cities/covington" },
    { name: "Same-day passport help in Jeffersontown", route: "/cities/jeffersontown" },
    { name: "Passport courier services near Elizabethtown", route: "/cities/elizabethtown" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Louisville remotely via secure courier. We do not have a walk-in office in Louisville — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Jefferson County travelers.",
  fpcOfficeDistance: "Serving all of Jefferson County and the Greater Louisville area including Southern Indiana",
  serviceAreaIntro: "From downtown Louisville to the Highlands, from the St. Matthews area to the cross-river Indiana communities — Fast Passport Center serves the entire Louisville metro area. We regularly help travelers from Jeffersonville, New Albany, and Clarksville get their passports fast.",
  nearbyCitiesIntro: "We serve all Louisville neighborhoods and nearby Greater Louisville cities including Jeffersonville (IN), New Albany (IN), Clarksville (IN), St. Matthews, Jeffersontown, Shively, Shelbyville, Elizabethtown, Bardstown, Radcliff, and Fort Knox. If you are in Jefferson County, Southern Indiana, or the Derby City region, we have you covered.",
  howItWorksIntro: "Most Louisville travelers complete everything online and ship documents overnight. No need to drive to Chicago or wait in line at the regional agency.",
  mistakesIntro: "Louisville travelers often face long drives to the nearest regional passport agency. Here are the most common mistakes that cause delays.",
  testimonialsIntro: "Louisville and Southern Indiana travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Louisville? Here are the most common ones we hear from Jefferson County travelers.",
  statePageRoute: "/states/kentucky",
  statePageName: "Kentucky",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "3–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns and budget fights",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Jefferson County international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Patricia W.",
      location: "Louisville, KY",
      rating: 5,
      text: "Derby week travel to London and my passport had expired. Fast Passport Center renewed it in 3 days. I made the flight and saw the Royal Ascot. Absolutely incredible service for Derby City travelers.",
      service: "Passport Renewal",
    },
    {
      name: "Robert H.",
      location: "Jeffersonville, IN",
      rating: 5,
      text: "I live across the river in Indiana and wasn't sure if I could use a Kentucky service. They explained everything and shipped me a prepaid label. My passport arrived in 5 days. Professional and reliable.",
      service: "New Passport",
    },
    {
      name: "Jennifer L.",
      location: "St. Matthews, KY",
      rating: 5,
      text: "Our family cruise to the Caribbean was in 10 days and my husband's passport was lost. They got him an emergency replacement in 36 hours. We made our cruise from Miami. Worth every penny.",
      service: "Lost Passport",
    },
    {
      name: "Marcus T.",
      location: "New Albany, IN",
      rating: 5,
      text: "Emergency family situation in Mexico required travel within 72 hours. Fast Passport Center processed my emergency passport in 24 hours. Their courier service is the real deal for cross-river travelers.",
      service: "Emergency Passport",
    },
  ],
  facilities: [
    {
      id: "louisville-1",
      name: "Louisville Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1420 W Liberty St",
      city: "Louisville",
      state: "KY",
      zip: "40203",
      phone: "(502) 582-1431",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown Louisville",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Major facility for Jefferson County. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "louisville-2",
      name: "Jeffersonville Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "331 W Court Ave",
      city: "Jeffersonville",
      state: "IN",
      zip: "47130",
      phone: "(812) 282-1511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.3 miles from downtown Jeffersonville",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Southern Indiana residents. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "louisville-3",
      name: "St. Matthews Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "4600 Shelbyville Rd",
      city: "Louisville",
      state: "KY",
      zip: "40207",
      phone: "(502) 895-1521",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.2 miles from St. Matthews shopping district",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves eastern Louisville. Photo services available. Book ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "louisville-4",
      name: "Jeffersontown Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "10420 Watterson Trail",
      city: "Jeffersontown",
      state: "KY",
      zip: "40299",
      phone: "(502) 266-1511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.3 miles from Jeffersontown center",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Jeffersontown area. Full passport services available.",
      icon: "ri-mail-line",
    },
    {
      id: "louisville-5",
      name: "Shively Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "3620 Dixie Hwy",
      city: "Louisville",
      state: "KY",
      zip: "40216",
      phone: "(502) 447-1521",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.2 miles from Shively center",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves southwestern Louisville. Convenient for Shively area residents.",
      icon: "ri-mail-line",
    },
    {
      id: "louisville-6",
      name: "Clarksville Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1001 Little League Blvd",
      city: "Clarksville",
      state: "IN",
      zip: "47129",
      phone: "(812) 282-1111",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.3 miles from downtown Clarksville",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Alternative for Southern Indiana residents. Extended hours available.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 5 hour drive to Chicago", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Louisville area photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Jefferson County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Louisville applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the Chicago regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Jefferson County families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Derby City travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Louisville." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Louisville address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Louisville before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Louisville for travelers with urgent international flights. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, achieving 24–48 hour turnaround — without the 5 hour drive to Chicago." },
    { q: "How fast can I renew my passport in Louisville?", a: "Fast Passport Center offers fast passport renewal in Louisville in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day expedited option provides the best value. Both options include full document review and a dedicated agent." },
    { q: "What if my passport expires before my cruise?", a: "If your passport expires before your cruise, contact Fast Passport Center immediately. Many cruise lines require your passport to be valid for at least 6 months beyond your travel date. We can expedite your passport renewal in Louisville in 24–48 hours so you don't miss your departure." },
    { q: "Can I get a same-day passport appointment near Louisville?", a: "Same-day passport appointments are only available at the Chicago Regional Passport Agency — a 5 hour drive from Louisville — for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Jefferson County residents." },
    { q: "Can I expedite a child passport in Louisville?", a: "Yes. Fast Passport Center expedites child passports in Louisville for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs. We walk Jefferson County families through the entire process." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Louisville with DS-64 form guidance and identity verification support. Our emergency courier service can get your replacement passport in 24–48 hours." },
    { q: "How fast can I get a passport in Louisville?", a: "With Fast Passport Center's emergency courier service, Louisville travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays." },
    { q: "Do I need to drive to the Chicago Passport Agency?", a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change." },
    { q: "Where are the closest passport acceptance facilities in Louisville?", a: "Popular options include the Louisville Main Post Office (40203), Jeffersonville Post Office (IN), St. Matthews Post Office (40207), Jeffersontown Post Office (40299), Shively Post Office (40216), and Clarksville Post Office (IN). All require appointments. We help you find the most convenient location and schedule your visit." },
    { q: "Can I get a same-day passport in Louisville?", a: "Same-day passports are only available at the Chicago Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option — without the 5 hour drive to Chicago." },
    { q: "What if I live in Southern Indiana?", a: "We serve the entire Greater Louisville metro including Jeffersonville, New Albany, Clarksville, and surrounding Southern Indiana communities. Our overnight shipping labels work from any UPS location, so your location on either side of the river doesn't affect processing speed." },
    { q: "How much does expedited passport service cost in Louisville?", a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline." },
    { q: "Do you help with passport photos in Louisville?", a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and FedEx locations in Louisville and Jeffersonville offer passport photo services. We review your photos before submission to ensure they meet State Department standards." },
    { q: "What documents do I need for a first-time passport in Louisville?", a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk." },
    { q: "Can you help with a child's passport in Louisville?", a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Jefferson County families through the entire process and help schedule acceptance clerk appointments." },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Louisville", address: "4911 Brownsboro Rd, Louisville, KY 40222", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Louisville", address: "3600 Bardstown Rd, Louisville, KY 40218", hours: "Mon–Sun: 7AM–11PM" },
    { name: "Walmart — Louisville", address: "7100 Raggard Rd, Louisville, KY 40216", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Louisville", address: "2640 Hikes Ln, Louisville, KY 40218", hours: "Mon–Sun: 8AM–10PM" },
    { name: "CVS Pharmacy — Jeffersonville", address: "2720 Charlestown Rd, Jeffersonville, IN 47130", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walmart — Jeffersonville", address: "2020 Allison Ln, Jeffersonville, IN 47130", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Walgreens — St. Matthews", address: "3939 Shelbyville Rd, Louisville, KY 40207", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Louisville", address: "9600 Preston Crossing Blvd, Louisville, KY 40229", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
  ],
  dmvLocations: [
    {
      name: "Circuit Court Clerk — Jefferson County",
      address: "600 W Jefferson St, Louisville, KY 40202",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Driver's license and REAL ID. Appointment recommended.",
      featured: true,
    },
    {
      name: "Circuit Court Clerk — Clark County (IN)",
      address: "501 E Court Ave, Jeffersonville, IN 47130",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "REAL ID and driver's license renewals for Indiana residents. Online booking available.",
      featured: false,
    },
    {
      name: "Circuit Court Clerk — Floyd County (IN)",
      address: "311 Hauss Square, New Albany, IN 47150",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Full-service center for Floyd County residents.",
      featured: true,
    },
    {
      name: "Circuit Court Clerk — Bullitt County",
      address: "250 Country Ln, Shepherdsville, KY 40165",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Alternative for Bullitt County residents. Walk-ins limited.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "SDF",
      name: "Louisville Muhammad Ali International",
      distance: "8 miles from downtown Louisville",
      address: "600 Terminal Dr, Louisville, KY 40209",
      note: "The primary airport for Jefferson County and the Greater Louisville area. Domestic flights with connections to major international hubs.",
      flightUrl: "https://www.flylouisville.com/",
      flightLabel: "Louisville Airport",
      featured: true,
    },
    {
      code: "LEX",
      name: "Blue Grass Airport (Lexington)",
      distance: "80 miles from Louisville",
      address: "4000 Terminal Dr, Lexington, KY 40510",
      note: "Alternative regional airport with flights to major hubs.",
      flightUrl: "https://www.bluegrassairport.com/",
      flightLabel: "Blue Grass Airport",
      featured: false,
    },
    {
      code: "CVG",
      name: "Cincinnati/Northern Kentucky International",
      distance: "100 miles from Louisville",
      address: "3087 Terminal Dr, Hebron, KY 41048",
      note: "Major international hub for long-haul flights to Europe, Asia, and South America. Accessible via I-71.",
      flightUrl: "https://www.cvgairport.com/",
      flightLabel: "CVG Airport",
      featured: false,
    },
  ],
};